import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo } from "react";
import { useOutletContext, useSearchParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import { isScanRunInProgress } from "shared/Scan/access/useCanTriggerScan";
import { SearchQueryRequestedPage } from "types/generated";

import {
  SCAN_RESOURCES_LIMIT,
  SCAN_RESOURCES_LIMIT_STORAGE_KEY,
} from "../components/Table/constants";
import {
  SCAN_RESOURCES_INITIAL_SORT_DIRECTION,
  SCAN_RESOURCES_INITIAL_SORT_OPTION,
} from "../constants";
import { getScanResourceRegion } from "../helpers";
import { ScanResourcesListItem, ScanResourcesOutletContext } from "../types";
import { SEARCH_SCAN_RESOURCES } from "./gql";
import useScanResourcesRefetchOnRunFinish from "./useRefetchOnRunFinish";

const useScanResources = () => {
  const { onError } = useTypedContext(FlashContext);
  const { run } = useOutletContext<ScanResourcesOutletContext>();
  const [searchParams] = useSearchParams();
  const [storedRowsPerPage] = useLocalStorage<number>(SCAN_RESOURCES_LIMIT_STORAGE_KEY);

  const fullTextSearch = getSearchQuery(searchParams);
  const runId = run?.id;

  const predicates = useMemo(
    () => [...(getFiltersPredicationFromURI(searchParams, true)?.values() || [])],
    [searchParams]
  );

  const orderBy = useMemo(
    () =>
      getSortOptionFromURI(
        searchParams,
        SCAN_RESOURCES_INITIAL_SORT_OPTION,
        SCAN_RESOURCES_INITIAL_SORT_DIRECTION
      ),
    [searchParams]
  );

  const initialVariables = useMemo(
    () => ({
      id: runId || "",
      input: {
        first: storedRowsPerPage || SCAN_RESOURCES_LIMIT,
        after: null,
        fullTextSearch,
        predicates,
        orderBy,
        requestedPage: null,
      },
    }),
    [runId, storedRowsPerPage, fullTextSearch, predicates, orderBy]
  );

  const { error, loading, data, previousData, networkStatus, refetch } = usePolledQuery(
    SEARCH_SCAN_RESOURCES,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      shouldPoll: () => isScanRunInProgress(run?.state),
      nextFetchPolicy: "cache-first",
      skip: !runId,
    }
  );

  useScanResourcesRefetchOnRunFinish(refetch);

  const loadScanResources = useCallback(
    async ({
      cursor,
      rowsPerPage,
      requestedPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      if (!runId) {
        return;
      }

      try {
        await refetch({
          id: runId,
          input: {
            ...initialVariables.input,
            after: cursor,
            first: rowsPerPage,
            requestedPage,
          },
        });
      } catch (refetchError) {
        onError(refetchError);
      }
    },
    [onError, initialVariables, refetch, runId]
  );

  const searchResources = data?.scanRun.searchResources || previousData?.scanRun.searchResources;

  const resources: ScanResourcesListItem[] = useMemo(
    () =>
      searchResources?.edges.map(({ node }) => ({
        id: node.id,
        name: node.name,
        type: node.type,
        isManaged: node.isManaged,
        region: getScanResourceRegion(node.identity),
      })) || [],
    [searchResources?.edges]
  );

  const hasRows = resources.length > 0;

  // Only skeleton when there is nothing to show yet. Filter/sort URL updates trigger
  // setVariables; keep the previous rows visible instead of flashing an empty skeleton.
  const loadingContent =
    !!runId &&
    loading &&
    !searchResources &&
    (networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables);

  const loadingPagination =
    !!runId &&
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const isFiltered = fullTextSearch.length > 0 || predicates.length > 0;

  const paginationResetKey = useMemo(
    () => ({ runId, fullTextSearch, predicates, orderBy }),
    [runId, fullTextSearch, predicates, orderBy]
  );

  return {
    resources,
    error,
    loadingContent,
    loadingPagination,
    hasRows,
    hasNoResults: !!searchResources && !hasRows && isFiltered && !loadingContent,
    loadScanResources,
    endCursor: searchResources?.pageInfo.endCursor,
    startCursor: searchResources?.pageInfo.startCursor,
    paginationResetKey,
  };
};

export default useScanResources;
